// Stabilization directive for the migration build.
// Replaces vue-infinite-scroll while preserving the existing attribute API.

const stateKey = '__wwInfiniteScroll';

function throttle(fn, delay) {
    let timer = null;
    let lastRun = 0;

    return function throttled() {
        const now = Date.now();
        const remaining = delay - (now - lastRun);

        if (timer) {
            clearTimeout(timer);
            timer = null;
        }

        if (remaining <= 0) {
            lastRun = now;
            fn();
        } else {
            timer = setTimeout(() => {
                lastRun = Date.now();
                fn();
            }, remaining);
        }
    };
}

function getScrollTop(element) {
    if (element === window) {
        return Math.max(window.pageYOffset || 0, document.documentElement.scrollTop);
    }

    return element.scrollTop;
}

function getVisibleHeight(element) {
    if (element === window) {
        return document.documentElement.clientHeight;
    }

    return element.clientHeight;
}

function getElementTop(element) {
    if (element === window) {
        return getScrollTop(window);
    }

    return element.getBoundingClientRect().top + getScrollTop(window);
}

function getScrollEventTarget(element) {
    let currentNode = element;

    while (
        currentNode &&
        currentNode.tagName !== 'HTML' &&
        currentNode.tagName !== 'BODY' &&
        currentNode.nodeType === 1
    ) {
        const overflowY = window.getComputedStyle(currentNode).overflowY;
        if (overflowY === 'scroll' || overflowY === 'auto') {
            return currentNode;
        }
        currentNode = currentNode.parentNode;
    }

    return window;
}

function readOption(instance, rawValue, fallback) {
    if (rawValue == null || rawValue === '') {
        return fallback;
    }

    if (instance && Object.prototype.hasOwnProperty.call(instance, rawValue)) {
        return instance[rawValue];
    }

    return rawValue;
}

function readNumberOption(instance, rawValue, fallback) {
    const value = Number(readOption(instance, rawValue, fallback));
    return Number.isNaN(value) ? fallback : value;
}

function readBooleanOption(instance, rawValue, fallback) {
    const value = readOption(instance, rawValue, fallback);

    if (value === 'false') {
        return false;
    }

    return Boolean(value);
}

function isDisabled(state) {
    const rawValue = state.el.getAttribute('infinite-scroll-disabled');
    return readBooleanOption(state.instance, rawValue, false);
}

function shouldTrigger(state) {
    const scrollEventTarget = state.scrollEventTarget;
    const element = state.el;
    const viewportScrollTop = getScrollTop(scrollEventTarget);
    const viewportBottom = viewportScrollTop + getVisibleHeight(scrollEventTarget);

    if (scrollEventTarget === element) {
        return scrollEventTarget.scrollHeight - viewportBottom <= state.distance;
    }

    const elementBottom =
        getElementTop(element) -
        getElementTop(scrollEventTarget) +
        element.offsetHeight +
        viewportScrollTop;

    return viewportBottom + state.distance >= elementBottom;
}

function runCheck(state, force = false) {
    if (!state || (!force && isDisabled(state))) {
        return;
    }

    if (shouldTrigger(state) && typeof state.handler === 'function') {
        state.handler();
    }
}

function mounted(el, binding) {
    const instance = binding.instance;
    const throttleDelay = readNumberOption(
        instance,
        el.getAttribute('infinite-scroll-throttle-delay'),
        200
    );

    const state = {
        el,
        instance,
        handler: binding.value,
        distance: readNumberOption(instance, el.getAttribute('infinite-scroll-distance'), 0),
        immediateCheck: readBooleanOption(
            instance,
            el.getAttribute('infinite-scroll-immediate-check'),
            true
        ),
        scrollEventTarget: getScrollEventTarget(el),
        scrollListener: null,
        eventListener: null,
    };

    state.scrollListener = throttle(() => runCheck(state), throttleDelay);
    state.scrollEventTarget.addEventListener('scroll', state.scrollListener);

    const eventName = el.getAttribute('infinite-scroll-listen-for-event');
    if (eventName) {
        state.eventListener = () => runCheck(state, true);
        window.addEventListener(eventName, state.eventListener);
    }

    el[stateKey] = state;

    const check = () => runCheck(state, true);
    if (instance && typeof instance.$nextTick === 'function') {
        instance.$nextTick(check);
    } else {
        setTimeout(check, 0);
    }
}

function updated(el, binding) {
    if (el[stateKey]) {
        el[stateKey].handler = binding.value;
        el[stateKey].distance = readNumberOption(
            binding.instance,
            el.getAttribute('infinite-scroll-distance'),
            0
        );
    }
}

function unmounted(el) {
    const state = el[stateKey];

    if (!state) {
        return;
    }

    state.scrollEventTarget.removeEventListener('scroll', state.scrollListener);

    if (state.eventListener) {
        window.removeEventListener(
            el.getAttribute('infinite-scroll-listen-for-event'),
            state.eventListener
        );
    }

    delete el[stateKey];
}

export default {
    mounted,
    updated,
    unmounted,
};
